import type { IndustryStructural } from "@/lib/types/industry";

interface Props {
  structural: IndustryStructural;
  relevantTailwinds?: string[]; // exact titles from stitch
  relevantHeadwinds?: string[];
}

export function IndustryStructuralCard({
  structural,
  relevantTailwinds = [],
  relevantHeadwinds = [],
}: Props) {
  const relevantT = new Set(relevantTailwinds);
  const relevantH = new Set(relevantHeadwinds);

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-slate-200">{structural.summary}</p>

      {structural.keyMetrics && structural.keyMetrics.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {structural.keyMetrics.map((m) => (
            <li key={m} className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300">
              {m}
            </li>
          ))}
        </ul>
      )}

      <Section
        heading="Tailwinds"
        items={structural.tailwinds}
        relevant={relevantT}
        accent="text-emerald-400"
        marker="▲"
      />

      <Section
        heading="Headwinds"
        items={structural.headwinds}
        relevant={relevantH}
        accent="text-rose-400"
        marker="▼"
      />
    </div>
  );
}

function Section({
  heading, items, relevant, accent, marker,
}: {
  heading: string;
  items: IndustryStructural["tailwinds"];
  relevant: Set<string>;
  accent: string;
  marker: string;
}) {
  if (items.length === 0) {
    return <p className="text-xs text-slate-500">Structural outlook pending.</p>;
  }
  return (
    <div>
      <h3 className={`mb-2 text-sm font-semibold ${accent}`}>
        {marker} {heading}
      </h3>
      <ul className="space-y-2">
        {items.map((f) => {
          const isRelevant = relevant.has(f.title);
          return (
            <li
              key={f.title}
              className={`rounded border p-3 ${
                isRelevant
                  ? "border-amber-500/40 bg-amber-500/5"
                  : "border-slate-700 bg-slate-900"
              }`}
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="font-medium text-slate-100">{f.title}</span>
                <span className="rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-slate-400">
                  {f.horizon}
                </span>
                {isRelevant && (
                  <span className="text-xs text-amber-400" aria-label="Most relevant">
                    ★ relevant
                  </span>
                )}
              </div>
              <p className="text-xs leading-relaxed text-slate-300">{f.detail}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
